/**
 * Iter121G — Add / Edit Lead
 * Routes:
 *   /cases/:caseId/leads/new
 *   /cases/:caseId/leads/:leadId/edit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Lightbulb, Loader2, RefreshCw, Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

export default function LeadFormPage() {
  const { caseId, leadId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const isEdit = Boolean(leadId);

  const [caseInfo, setCaseInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({ lead_source: '', description: '', priority: 'MEDIUM', status: 'NEW', notes: '' });
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCaseInfo).catch(() => {});
  }, [caseId, token]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const l = await res.json();
      setForm({
        lead_source: l.lead_source || '',
        description: l.description || '',
        priority: l.priority || 'MEDIUM',
        status: l.status || 'NEW',
        notes: l.notes || '',
      });
    } catch (e) {
      toast({ title: 'Failed to load lead', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [isEdit, leadId, token]);

  useEffect(() => { load(); }, [load]);

  const goBack = () => navigate(`/cases/${caseId}?tab=leads`);

  const submit = async () => {
    if (!form.description?.trim()) {
      toast({ title: 'Missing description', description: 'Description is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const url = isEdit ? `${BACKEND_URL}/api/leads/${leadId}` : `${BACKEND_URL}/api/cases/${caseId}/leads`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      toast({ title: isEdit ? 'Lead updated' : 'Lead added' });
      isEdit ? navigate(`/cases/${caseId}/leads/${leadId}/view`) : goBack();
    } catch (e) {
      toast({ title: isEdit ? 'Could not update lead' : 'Could not add lead', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading lead…
      </div>
    );
  }

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="lead-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="lead-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-300" /> {isEdit ? 'Edit Lead' : 'Add Lead'}
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">Lead Source</Label>
            <Input value={form.lead_source} onChange={e => setF('lead_source', e.target.value)}
              className="bg-slate-800 border-slate-600 text-white"
              placeholder="Anonymous tip, informant, client, records..." />
          </div>
          <div>
            <Label className="text-slate-300">Priority</Label>
            <Select value={form.priority} onValueChange={v => setF('priority', v)}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {isEdit && (
          <div>
            <Label className="text-slate-300">Status</Label>
            <Select value={form.status} onValueChange={v => setF('status', v)}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NEW">New</SelectItem>
                <SelectItem value="INVESTIGATING">Investigating</SelectItem>
                <SelectItem value="VERIFIED">Verified</SelectItem>
                <SelectItem value="DISMISSED">Dismissed</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-slate-300">Description *</Label>
          <Textarea value={form.description} onChange={e => setF('description', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={5}
            placeholder="Describe the lead..." data-testid="lead-description-input" />
        </div>
        <div>
          <Label className="text-slate-300">Notes</Label>
          <Textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={3} />
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="lead-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="lead-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving || !form.description?.trim()}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="submit-lead-btn">
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
            : (isEdit ? <><Save className="w-4 h-4 mr-1.5" /> Save Changes</> : 'Add Lead')}
        </Button>
      </div>
    </div>
  );
}
